import ProposalApprovalConfirmDialog from "@/components/proposal/ProposalApprovalConfirmDialog";
import ProposalPdfDownloadButton from "@/components/proposal/ProposalPdfDownloadButton";
import { proposalPdfDownloadLabel, shouldSkipProposalCustomerEngagementTracking } from "@shared/proposalCustomerEngagement";
import { Button } from "@/components/ui/button";
import { buildSalespersonContactHref } from "@/lib/proposalCustomerActions";
import {
  proposalApprovalSuccessPath,
  proposalApprovalSuccessPathForCredential,
  saveProposalApprovalSuccess,
} from "@/lib/proposalApprovalSuccess";
import { trpc } from "@/lib/trpc";
import type { ProposalCustomerPricingDisplay } from "@shared/proposalCustomerPricing";
import {
  DEFAULT_APPROVAL_WHATSAPP_COUNTRY,
  resolveApprovalWhatsAppCountry,
  seedApprovalWhatsAppFromContact,
} from "@shared/proposalApprovalOtp";
import { MessageCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { toast } from "sonner";

export type DtoolsOptionSectionActionsProps = {
  optionLabel: string;
  proposalId?: number;
  proposalRef?: string;
  customerName?: string | null;
  customerEmail?: string | null;
  customerContact?: string | null;
  customerPricing?: ProposalCustomerPricingDisplay | null;
  hideCombinedProposalPricing?: boolean;
  proposalStatus?: string;
  approvalDisabled?: boolean;
  isSandbox?: boolean;
  onOpenCleo?: () => void;
  salesperson?: {
    name?: string | null;
    email?: string | null;
    contact?: string | null;
  } | null;
  /** Full footer CTA vs compact approve button for summary blocks. */
  variant?: "full" | "approve-only" | "system-page-footer" | "approve-inline";
  /** Multi-option proposals: clearer approve label and option-specific modal copy. */
  multiOptionApproval?: boolean;
  /** Secure Link B credential — required for public (non-staff) approval / download actions. */
  linkBCredential?: { readableRef: string; sequence: number; token: string } | null;
};

const APPROVE_CTA_LABEL_DEFAULT = "CLICK HERE TO APPROVE THIS PROPOSAL";
const APPROVE_CTA_LABEL_MULTI_OPTION = "APPROVE THIS PROPOSAL";
const APPROVE_CTA_LABEL_INLINE = "APPROVE";

export default function DtoolsOptionSectionActions({
  optionLabel,
  proposalId,
  proposalRef = "",
  customerName,
  customerEmail,
  customerContact,
  customerPricing,
  hideCombinedProposalPricing = false,
  proposalStatus,
  approvalDisabled = false,
  isSandbox = false,
  onOpenCleo,
  salesperson,
  variant = "full",
  multiOptionApproval = false,
  linkBCredential,
}: DtoolsOptionSectionActionsProps) {
  const approveCtaLabel =
    variant === "approve-inline"
      ? APPROVE_CTA_LABEL_INLINE
      : multiOptionApproval && variant !== "full"
        ? APPROVE_CTA_LABEL_MULTI_OPTION
        : APPROVE_CTA_LABEL_DEFAULT;
  const [, setLocation] = useLocation();
  const [approveOpen, setApproveOpen] = useState(false);
  const [otpStep, setOtpStep] = useState<"details" | "otp">("details");
  const [approverName, setApproverName] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [whatsappCountry, setWhatsappCountry] = useState(DEFAULT_APPROVAL_WHATSAPP_COUNTRY);
  const [approvalEmail, setApprovalEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [maskedWhatsApp, setMaskedWhatsApp] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpNotice, setOtpNotice] = useState<string | null>(null);
  const [resendCooldownSec, setResendCooldownSec] = useState(0);
  const seeded = useRef(false);

  useEffect(() => {
    if (seeded.current) return;
    if (customerName?.trim()) setApproverName(customerName.trim());
    if (customerContact?.trim()) {
      const seededPhone = seedApprovalWhatsAppFromContact(customerContact);
      setWhatsappCountry(seededPhone.country);
      setWhatsappNumber(seededPhone.number);
    }
    if (customerEmail?.trim()) setApprovalEmail(customerEmail.trim());
    seeded.current = true;
  }, [customerName, customerContact, customerEmail]);

  useEffect(() => {
    if (resendCooldownSec <= 0) return;
    const t = window.setTimeout(() => setResendCooldownSec((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearTimeout(t);
  }, [resendCooldownSec]);

  const requestOtp = trpc.proposals.requestPublicProposalApprovalOtp.useMutation({
    onMutate: () => {
      setOtpNotice(null);
      setOtpError(null);
    },
    onSuccess: (res) => {
      setMaskedWhatsApp(res.maskedWhatsApp);
      if (res.rateLimited) {
        setOtpStep("otp");
        setResendCooldownSec(Math.max(1, res.retryAfterSeconds ?? 60));
        const message = "Too many codes have been requested. Please wait for the countdown, then try again.";
        setOtpError(message);
        toast.message(message);
        return;
      }
      const wasResend = otpStep === "otp";
      setOtpStep("otp");
      setOtpCode("");
      setResendCooldownSec(Math.max(1, res.retryAfterSeconds ?? 30));
      setOtpNotice(
        wasResend
          ? "A new WhatsApp code has been requested. Only the newest code will work."
          : "Your WhatsApp verification code is on its way.",
      );
      toast.success(
        wasResend
          ? "A new WhatsApp code is on its way."
          : "Your WhatsApp verification code is on its way.",
      );
      if ("devOtp" in res && typeof (res as { devOtp?: string }).devOtp === "string") {
        setOtpCode((res as { devOtp: string }).devOtp);
      }
    },
    onError: (e) => {
      const message = e.message || "We could not send a verification code. Please try again.";
      setOtpError(message);
      toast.error(message);
    },
  });

  const approveProposal = trpc.proposals.approvePublicProposal.useMutation({
    onSuccess: (res) => {
      const payload = (res as { successScreen?: Parameters<typeof saveProposalApprovalSuccess>[0] }).successScreen;
      if (payload && proposalId) {
        saveProposalApprovalSuccess(payload);
        setLocation(
          linkBCredential
            ? proposalApprovalSuccessPathForCredential(linkBCredential)
            : proposalApprovalSuccessPath(proposalId),
        );
        return;
      }
      setApproveOpen(false);
      toast.success("Thank you — your proposal is approved.");
    },
    onError: (e) => {
      const friendly =
        e.message || "That code could not be verified. Please try again or request a new code.";
      setOtpError(friendly);
      toast.error(friendly);
    },
  });

  const contactHref = buildSalespersonContactHref({
    proposalRef,
    email: salesperson?.email,
    contact: salesperson?.contact,
  });

  const isApproved = proposalStatus === "approved";
  const canApprove =
    Boolean(proposalId) &&
    Boolean(customerPricing) &&
    !isSandbox &&
    !approvalDisabled &&
    !isApproved;
  const approveBusy = approveProposal.isPending || requestOtp.isPending;

  const openApproveDialog = () => {
    if (!canApprove) return;
    setOtpStep("details");
    setOtpCode("");
    setOtpError(null);
    setOtpNotice(null);
    setApproveOpen(true);
  };

  const handleQuestions = () => {
    if (onOpenCleo) {
      onOpenCleo();
      return;
    }
    if (contactHref) {
      window.open(contactHref, "_blank", "noopener,noreferrer");
      return;
    }
    toast.message("Contact your Clarity consultant", {
      description: salesperson?.email?.trim() || salesperson?.contact?.trim() || "We will respond shortly.",
    });
  };

  const approvalDialog = customerPricing ? (
    <ProposalApprovalConfirmDialog
      open={approveOpen}
      onOpenChange={(open) => {
        setApproveOpen(open);
        if (!open) {
          setOtpStep("details");
          setOtpCode("");
          setOtpError(null);
          setOtpNotice(null);
        }
      }}
      proposalRef={proposalRef}
      optionLabel={multiOptionApproval ? optionLabel : undefined}
      customerPricing={customerPricing}
      hidePricingSummary={hideCombinedProposalPricing}
      approvalEmail={approvalEmail}
      onApprovalEmailChange={setApprovalEmail}
      confirming={approveProposal.isPending}
      confirmDisabled={isApproved}
      onConfirm={() => undefined}
      otpStep={otpStep}
      approverName={approverName}
      onApproverNameChange={setApproverName}
      whatsappNumber={whatsappNumber}
      onWhatsappNumberChange={setWhatsappNumber}
      whatsappCountry={whatsappCountry}
      onWhatsappCountryChange={(value) => setWhatsappCountry(resolveApprovalWhatsAppCountry(value))}
      otpCode={otpCode}
      onOtpCodeChange={setOtpCode}
      maskedWhatsApp={maskedWhatsApp}
      sendingOtp={requestOtp.isPending}
      verifyingOtp={approveProposal.isPending}
      resendCooldownSec={resendCooldownSec}
      otpNotice={otpNotice}
      otpError={otpError}
      onSendOtp={() => {
        if (!linkBCredential) return;
        requestOtp.mutate({
          credential: linkBCredential,
          approverName,
          whatsappNumber,
          whatsappCountry,
          approvalEmail: approvalEmail.trim() || undefined,
        });
      }}
      onVerifyOtp={() => {
        if (!linkBCredential) return;
        setOtpError(null);
        approveProposal.mutate({
          credential: linkBCredential,
          approverName,
          whatsappNumber,
          whatsappCountry,
          approvalEmail: approvalEmail.trim() || undefined,
          otpCode: otpCode.trim(),
        });
      }}
      onBackToDetails={() => {
        setOtpStep("details");
        setOtpCode("");
        setOtpError(null);
        setOtpNotice(null);
      }}
    />
  ) : null;

  if (variant === "approve-inline") {
    return (
      <div className="shrink-0" data-testid="dtools-option-section-actions-inline">
        <Button
          type="button"
          size="sm"
          className="h-8 whitespace-nowrap bg-[#1A2744] px-3 text-xs font-semibold uppercase tracking-wide text-white hover:bg-[#1A2744]/90"
          disabled={!canApprove || approveBusy}
          onClick={openApproveDialog}
        >
          {approveCtaLabel}
        </Button>
        {approvalDialog}
      </div>
    );
  }

  if (variant === "approve-only") {
    return (
      <div className="w-full" data-testid="dtools-option-section-actions">
        <Button
          type="button"
          size="lg"
          className="min-h-11 w-full bg-[#1A2744] uppercase tracking-wide text-white hover:bg-[#1A2744]/90"
          disabled={!canApprove || approveBusy}
          onClick={openApproveDialog}
        >
          {approveCtaLabel}
        </Button>
        {approvalDialog}
      </div>
    );
  }

  if (variant === "system-page-footer") {
    return (
      <div
        className="w-full space-y-4 border-t border-[#1A2744]/10 pt-10"
        data-testid="dtools-option-section-actions"
      >
        <div className="flex w-full flex-col gap-3 sm:flex-row">
          {proposalId ? (
            <ProposalPdfDownloadButton
              proposalId={proposalId}
              proposalRef={proposalRef}
              customerEmail={customerEmail}
              linkBCredential={linkBCredential}
              skipAnalytics={shouldSkipProposalCustomerEngagementTracking({
                approvalDisabled,
                isSandbox,
              })}
              variant="outline"
              label={proposalPdfDownloadLabel({
                approvalDisabled,
                isSandbox,
              })}
              className="min-h-11 flex-1 border-[#1A2744]/20 text-[#1A2744]"
            />
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="min-h-11 flex-1 border-[#1A2744]/20 text-[#1A2744]"
            onClick={handleQuestions}
          >
            Questions?
          </Button>
        </div>
        <Button
          type="button"
          size="lg"
          className="min-h-11 w-full bg-[#1A2744] uppercase tracking-wide text-white hover:bg-[#1A2744]/90"
          disabled={!canApprove || approveBusy}
          onClick={openApproveDialog}
        >
          {approveCtaLabel}
        </Button>
        {approvalDialog}
      </div>
    );
  }

  return (
    <div
      className="mt-10 flex flex-col items-center gap-4 border-t border-[#1A2744]/10 pt-10"
      data-testid="dtools-option-section-actions"
    >
      <p className="text-center text-sm text-[#1A2744]/70">
        {isSandbox ? "Customer actions preview" : `Ready to proceed with ${optionLabel}?`}
      </p>
      <div className="flex w-full max-w-md flex-col items-stretch gap-3 sm:flex-row sm:justify-center">
        <Button
          type="button"
          variant="outline"
          size="lg"
          className="min-h-11 border-[#1A2744]/20 text-[#1A2744]"
          onClick={handleQuestions}
        >
          <MessageCircle className="mr-2 h-4 w-4" />
          Questions?
        </Button>
        <Button
          type="button"
          size="lg"
          className="min-h-11 bg-[#1A2744] text-white hover:bg-[#1A2744]/90"
          disabled={!canApprove || approveBusy}
          onClick={openApproveDialog}
        >
          {APPROVE_CTA_LABEL_DEFAULT}
        </Button>
      </div>

      {approvalDialog}
    </div>
  );
}
